import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Me, MediaResponse } from 'types';
import { useMe } from 'utils/util/hooks';
import { AdvancedCurrentPeopleFilterRoleUserApiComponent } from 'views/Widgets/Filters/AdvancedSpecializedFilter/highOrder';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import ModalEditor from 'views/Widgets/Modals/ModalEditor';

interface Props {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: (createdDocument: unknown) => void;
}

const CreateDocumentModal: FC<Props> = ({ isOpen, toggle, onSuccess }) => {
  const { formatMessage } = useIntl();
  const me: Me | undefined = useMe();
  const handleCallback = useCallback(
    (data: unknown, error: unknown) => {
      if (data) {
        toast.success(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.create_modal.success',
            defaultMessage: 'Document created successfully',
          })
        );
        onSuccess(data);
        toggle();
      } else if (error) {
        toast.error(
          formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.create_modal.error',
            defaultMessage: 'Failed to create document',
          })
        );
      }
    },
    [formatMessage, onSuccess, toggle]
  );

  const inputs = useMemo(
    () => [
      {
        required: true,
        name: 'name',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.name.label',
          defaultMessage: 'Document Name',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.name.placeholder',
          defaultMessage: 'Enter document name',
        }),
        type: INPUT_TYPES.TEXT,
        ignorePasswordManagers: true,
        autoFocus: true,
      },
      {
        required: false,
        name: 'description',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.description.label',
          defaultMessage: 'Description',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.description.help_text',
          defaultMessage: 'Provide a brief description of the document.',
        }),
        placeholder: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.description.placeholder',
          defaultMessage: 'Enter a description (optional)',
        }),
        type: INPUT_TYPES.TEXTAREA,
      },
      {
        required: true,
        allowSelf: true,
        name: 'owners',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.owners.label',
          defaultMessage: 'Owners',
        }),
        type: INPUT_TYPES.PEOPLE_EDITOR,
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.owners.help_text',
          defaultMessage:
            'Select people who can manage this document. If left empty, you will be set as the owner.',
        }),
      },
      {
        required: true,
        name: 'visibility',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.visibility.label',
          defaultMessage: 'Visibility',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.visibility.help_text',
          defaultMessage:
            'Set the visibility that will determine who can access the document. The people with this access will be able to view it but not edit it. If set to custom, you must define a viewer filter.',
        }),
        type: INPUT_TYPES.DROPDOWN,
        objects: [
          {
            id: 'I',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.private',
              defaultMessage: 'Private',
            }),
          },
          {
            id: 'P',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.public',
              defaultMessage: 'Public',
            }),
          },
          {
            id: 'C',
            name: formatMessage({
              id: 'app.views.administration.content_administration.documents_admin.visibility.custom',
              defaultMessage: 'Custom',
            }),
          },
        ],
        defaultValue: 'I',
      },
      {
        required: false,
        type: INPUT_TYPES.CUSTOM_COMPONENT,
        component: AdvancedCurrentPeopleFilterRoleUserApiComponent,
        name: 'viewers',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.viewers.label',
          defaultMessage: 'Viewers',
        }),
        helperText: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.viewers.help_text',
          defaultMessage:
            'Use advanced filters to specify which people can access this document.',
        }),
      },
      {
        required: true,
        name: 'media',
        label: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.media.label',
          defaultMessage: 'File',
        }),
        type: INPUT_TYPES.MEDIA_UPLOADER,
        uploadUrl: process.env.REACT_APP_CONFIRM_API_URL + '/files/document',
        multiple: true,
        maxFiles: 1,
        mediaResponseMapper: (mediaResponse: MediaResponse) => ({
          id: mediaResponse.media.id,
          description: mediaResponse?.description,
        }),
        callToAction: formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.fields.media.call_to_action',
          defaultMessage: 'Upload document file',
        }),
      },
    ],
    [formatMessage]
  );

  const transformObjectBeforeSubmit = useCallback(
    (obj: Record<string, unknown>) => {
      return {
        ...obj,
        media: (obj.media as Array<{ id: string }>).map((m) => m.id)[0],
        owners: (obj.owners as Array<{ id: string }>).map((o) => o.id),
      };
    },
    []
  );

  const renderInputs = useCallback(
    ([name, description, owners, visibility, viewers, media]) => {
      const visibilityValue =
        visibility.props.value ?? visibility.props.defaultValue;
      return (
        <>
          {name}
          {description}
          {owners}
          {visibility}
          {visibilityValue === 'C' && viewers}
          {media}
        </>
      );
    },
    []
  );

  const handleOnValidate = useCallback(
    (obj) => {
      const errors: Record<string, string> = {};
      if (obj.visibility === 'C' && !obj.viewers) {
        errors.viewers = formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.validation.viewers_required',
          defaultMessage:
            'Please specify a filter when visibility is set to custom.',
        });
      }
      if (!obj.media || (Array.isArray(obj.media) && obj.media.length === 0)) {
        errors.media = formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.create_modal.validation.media_required',
          defaultMessage: 'Please upload a file.',
        });
      }
      return errors;
    },
    [formatMessage]
  );

  const initialObject = useMemo(() => ({ owners: [me] }), [me]);

  return (
    <ModalEditor
      isOpen={isOpen}
      toggle={toggle}
      object={initialObject}
      inputs={inputs}
      method="POST"
      url="documents"
      submitText={formatMessage({
        id: 'app.views.administration.content_administration.documents_admin.create_modal.submit_text',
        defaultMessage: 'Create',
      })}
      title={formatMessage({
        id: 'app.views.administration.content_administration.documents_admin.create_modal.title',
        defaultMessage: 'Create New Document',
      })}
      callback={handleCallback}
      onValidate={handleOnValidate}
      transformObjectBeforeSubmit={transformObjectBeforeSubmit}
      renderInputs={renderInputs}
      keepOpenOnSuccess={true}
    />
  );
};

export default CreateDocumentModal;
